import React, { ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { BaseDialogConfig } from '../../stores/dialogStore';

interface BaseDialogProps {
  config: BaseDialogConfig & { type?: string };
  onClose: () => void;
  children?: ReactNode;
  title?: ReactNode;
  content?: ReactNode;
  actions?: ReactNode;
  showCloseButton?: boolean;
  titleTypographyProps?: Record<string, unknown>;
}

/**
 * BaseDialog - Centralized dialog component that provides:
 * 1. Consistent styling and behavior across all dialogs
 * 2. Accessibility features (ARIA labels, focus management)
 * 3. Common props handling (maxWidth, fullWidth, etc.)
 * 4. Standardized close handling
 * 5. Flexible content rendering (children vs props)
 */
export function BaseDialog({
  config,
  onClose,
  children,
  title,
  content,
  actions,
  showCloseButton = false,
  titleTypographyProps = {},
}: BaseDialogProps) {
  const handleBackdropClick = (_event: object, reason: string) => {
    if (config.disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    // disableEscapeKeyDown was removed from MUI Dialog in v9; block escape here.
    if (config.disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  React.useEffect(() => {
    if (config.disableEscapeKeyDown) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (config.disableEscapeKeyDown && event.key === 'Escape') {
          event.preventDefault();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    return undefined;
  }, [config.disableEscapeKeyDown]);

  // If children is provided, render raw content (for complex layouts)
  if (children) {
    return (
      <Dialog
        open={config.open}
        onClose={handleBackdropClick}
        maxWidth={config.maxWidth || 'md'}
        fullWidth={config.fullWidth !== false}
        aria-labelledby={`${config.id}-dialog-title`}
        aria-describedby={`${config.id}-dialog-content`}
        slotProps={{
          paper: {
            sx: {
              minHeight: '120px',
              position: 'relative',
            },
            ...({ 'data-testid': `dialog-${config.type}` } as object),
          },
        }}
      >
        {children}
      </Dialog>
    );
  }

  // Standard structured dialog
  return (
    <Dialog
      open={config.open}
      onClose={handleBackdropClick}
      maxWidth={config.maxWidth || 'md'}
      fullWidth={config.fullWidth !== false}
      aria-labelledby={`${config.id}-dialog-title`}
      aria-describedby={`${config.id}-dialog-content`}
      slotProps={{
        paper: {
          sx: {
            minHeight: '120px',
            position: 'relative',
          },
          ...({ 'data-testid': `dialog-${config.type}` } as object),
        },
      }}
    >
      {/* Title Section */}
      {title && (
        <DialogTitle
          id={`${config.id}-dialog-title`}
          sx={{
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {}
          <Typography variant="h4" component="h2" {...titleTypographyProps}>
            {typeof title === 'string' ? title : title}
          </Typography>
          {showCloseButton && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          )}
        </DialogTitle>
      )}

      {/* Content Section */}
      {content && (
        <DialogContent id={`${config.id}-dialog-content`} sx={{ pb: 2 }}>
          {content}
        </DialogContent>
      )}

      {/* Actions Section */}
      {actions && (
        <DialogActions sx={{ px: 3, pb: 2 }}>{actions}</DialogActions>
      )}
    </Dialog>
  );
}

export default BaseDialog;
